import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  applyPaymentButtonsForPdf,
  formatPaymentCompletedDisplay,
} from './paymentButtonReadOnlyState';

const sanitizeFileName = (name) => {
  const base = String(name || 'document')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-') // windows-illegal
    .replace(/\s+/g, ' ')
    .slice(0, 180);
  return base.length ? base : 'document';
};

/**
 * Generate a PDF Blob + Blob URL from a Knowledge Base document.
 *
 * - Prefers `editorHtml` for fidelity.
 * - Falls back to `textContent`.
 * - Optional `fieldValues` and `signatures`: maps of id → value (or data URL for signatures).
 *   When provided, fillable fields and signature blocks in the HTML are replaced with
 *   the given values/images before rendering to PDF.
 * - Optional `signatureMeta`: map of signature block id → { signedAt: string (ISO), signatureId: string }.
 *   When provided, "Signed on [date], ID: [id]" is rendered under each signature for legal record.
 *
 * Returns: { blob, url, filename }
 */
function formatSignatureDate(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return Number.isNaN(d.getTime()) ? isoString : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return isoString;
  }
}

/**
 * Format date field value from yyyy-mm-dd (HTML5 date input) to mm/dd/yyyy
 */
function formatDateFieldValue(value) {
  if (!value || typeof value !== 'string') return value;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
  }
  return value;
}

function formatFormAnswerValue(val) {
  if (val && typeof val === 'object' && val.fileName) return val.fileName;
  if (Array.isArray(val)) return val.join(', ') || '—';
  return (val != null && String(val).trim()) ? String(val) : '—';
}

function replaceFormBlocksWithAnswers(prose, submittedFormAnswers) {
  if (!prose || !submittedFormAnswers || typeof submittedFormAnswers !== 'object') return;
  prose.querySelectorAll('[data-form-block]').forEach((el) => {
    const formId = el.getAttribute('data-form-id');
    const title = el.getAttribute('data-form-title') || 'Form';
    let fields = [];
    try {
      fields = JSON.parse(el.getAttribute('data-form-fields') || '[]');
    } catch {
      fields = [];
    }
    const answers = submittedFormAnswers[formId] || {};

    el.innerHTML = '';
    el.style.cssText = 'margin:1rem 0;padding:1.25rem;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc';

    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;gap:0.5rem;margin-bottom:0.75rem';
    header.innerHTML = `
      <span style="display:inline-flex;width:24px;height:24px;background:#dcfce7;border-radius:50%;align-items:center;justify-content:center;">
        <svg width="14" height="14" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </span>
      <strong style="font-size:1rem;color:#1e293b;">${escapeHtml(title)}</strong>
      <span style="font-size:0.75rem;color:#64748b;margin-left:auto;">Submitted</span>`;
    el.appendChild(header);

    const list = document.createElement('div');
    list.style.cssText = 'display:flex;flex-direction:column;gap:0.5rem';

    fields.forEach((field) => {
      const val = answers[field.id];
      const row = document.createElement('div');
      row.style.cssText = 'font-size:0.875rem';
      const label = document.createElement('div');
      label.style.cssText = 'font-weight:600;color:#475569;font-size:0.75rem;margin-bottom:0.15rem';
      label.textContent = field.label || field.type;
      const value = document.createElement('div');
      value.style.cssText = 'color:#1e293b';
      let displayValue = formatFormAnswerValue(val);
      if (field.type === 'date' && typeof val === 'string') {
        displayValue = formatDateFieldValue(val);
      }
      value.textContent = displayValue;
      row.appendChild(label);
      row.appendChild(value);
      list.appendChild(row);
    });

    el.appendChild(list);
  });
}

function buildHeaderHtml(organizationLogoUrl, organizationName) {
  if (!organizationLogoUrl || typeof organizationLogoUrl !== 'string') return '';
  const parts = [];
  parts.push(
    `<img src="${escapeHtml(organizationLogoUrl)}" alt="Organization logo" crossorigin="anonymous" style="max-height:56px;max-width:200px;display:block;object-fit:contain;flex-shrink:0" />`
  );
  if (organizationName && typeof organizationName === 'string') {
    parts.push(`<div style="font-size:18px;color:#374151;font-weight:500;margin-left:16px;line-height:1;padding-top:8px">${escapeHtml(organizationName)}</div>`);
  }
  const orgBlock = `<div style="display:flex;align-items:flex-start">${parts.join('')}</div>`;
  const line = `<div style="margin-top:20px;padding-bottom:12px;border-bottom:1px solid #e5e7eb"></div>`;
  return `${orgBlock}${line}`;
}

// PDF Margins in points (72 points = 1 inch)
const MARGIN_LEFT_PT = 54;           // 0.75" side margin
const MARGIN_RIGHT_PT = 54;          // 0.75" side margin
const MARGIN_TOP_FIRST_PT = 90;      // 1.25" top margin for first page (header/logo space)
const MARGIN_TOP_SUBSEQUENT_PT = 54; // 0.75" top margin for subsequent pages
const MARGIN_BOTTOM_PT = 54;         // 0.75" bottom margin

// Safety buffer to account for rendering differences between getBoundingClientRect and html2canvas
const SAFETY_BUFFER_PT = 5;          // ~7px buffer before break points

const LOGO_MAX_WIDTH_PT = 120;
const LOGO_MAX_HEIGHT_PT = 40;

async function drawLogoOnFirstPage(pdf, organizationLogoUrl, organizationName, pageWidthPt, pageHeightPt) {
  let logoDataUrl = null;
  try {
    const res = await fetch(organizationLogoUrl, { mode: 'cors' });
    if (!res.ok) return;
    const blob = await res.blob();
    logoDataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return;
  }
  if (!logoDataUrl) return;
  pdf.setPage(1);
  const imgProps = pdf.getImageProperties(logoDataUrl);
  let w = LOGO_MAX_WIDTH_PT;
  let h = (imgProps.height * w) / imgProps.width;
  if (h > LOGO_MAX_HEIGHT_PT) {
    h = LOGO_MAX_HEIGHT_PT;
    w = (imgProps.width * h) / imgProps.height;
  }
  pdf.addImage(logoDataUrl, 'PNG', MARGIN_LEFT_PT, MARGIN_TOP_FIRST_PT, w, h, undefined, 'FAST');
  // Organization name is only drawn in the HTML header (captured image); we do not draw it here to avoid duplicate text on the first page.
}

function waitForImages(container, timeoutMs = 5000) {
  const imgs = container.querySelectorAll('img[src^="http"]');
  if (!imgs.length) return Promise.resolve();
  return Promise.race([
    Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise((resolve, reject) => {
            if (img.complete && img.naturalHeight) return resolve();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // continue without logo
          })
      )
    ),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

// Block elements to protect from page breaks - includes nested content
// We capture headers, paragraphs, list items, blockquotes, code blocks, tables, and other block elements
const BLOCK_SELECTOR = [
  '.pdf-export-header',
  '.pdf-export-prose > *',           // Direct children
  '.pdf-export-prose li',             // List items (including nested)
  '.pdf-export-prose p',              // Paragraphs (including nested in divs/blockquotes)
  '.pdf-export-prose h1',
  '.pdf-export-prose h2',
  '.pdf-export-prose h3',
  '.pdf-export-prose h4',
  '.pdf-export-prose h5',
  '.pdf-export-prose h6',
  '.pdf-export-prose blockquote',
  '.pdf-export-prose pre',
  '.pdf-export-prose table',
  '.pdf-export-prose tr',             // Table rows
  '.pdf-export-prose [data-field-id]',      // Fillable fields
  '.pdf-export-prose [data-signature-block-id]', // Signature blocks
  '.pdf-export-prose [data-form-block]',    // Form blocks
  '.pdf-export-prose span',           // Inline text elements
  '.pdf-export-prose div',            // Nested divs
].join(', ');

export async function generatePdfFromDocument({
  title,
  fileName,
  editorHtml,
  textContent,
  fieldValues = {},
  signatures = {},
  signatureMeta = {},
  submittedFormAnswers = null,
  paymentButtonUrl = null,
  paymentComplete = false,
  paidLinkIds = null,
  paymentAmountCents = null,
  paymentCompletedAt = null,
  organizationLogoUrl = null,
  organizationName = null,
}) {
  const nameBase = sanitizeFileName(title || fileName || 'document');
  const filenameOut = `${nameBase}.pdf`;

  const html = (typeof editorHtml === 'string' && editorHtml.trim().length > 0)
    ? editorHtml
    : (typeof textContent === 'string' && textContent.trim().length > 0)
      ? `<pre style="white-space:pre-wrap;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial">${escapeHtml(textContent)}</pre>`
      : `<p style="font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;color:#6b7280">No content available.</p>`;

  const headerHtml = buildHeaderHtml(organizationLogoUrl, organizationName);
  const wrapperContent = headerHtml
    ? `<div class="pdf-export-header" style="margin-bottom:12px">${headerHtml}</div><div class="prose max-w-none pdf-export-prose">${html}</div>`
    : `<div class="prose max-w-none pdf-export-prose">${html}</div>`;

  // Create an offscreen render container so Tailwind/global CSS applies.
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = '794px'; // ~A4 @ 96dpi (8.27in * 96)
  container.style.background = 'white';
  container.style.color = '#111827';
  // Bottom padding provides safety margin for text that may not be fully captured by block detection
  container.style.padding = '0 0 24px 0';
  container.style.boxSizing = 'border-box';
  container.style.zIndex = '-1';
  container.innerHTML = `<div class="pdf-export-wrapper">${wrapperContent}</div>`;

  // Clean up field styling and inject values/signatures for PDF export
  const prose = container.querySelector('.pdf-export-prose');
  if (prose) {
    removeDuplicateMarkdownHeadingLines(prose);
    // Remove field styling (green background, dashed border) for all fields - always do this for PDF export
    prose.querySelectorAll('[data-field-id]').forEach((el) => {
      const id = el.getAttribute('data-field-id');
      const fieldType = el.getAttribute('data-field-type');
      // Inject field value if provided, or show type label if empty
      if (id != null && fieldValues[id] != null && fieldValues[id] !== '') {
        let displayValue = fieldValues[id];
        if (fieldType === 'date' && displayValue) {
          displayValue = formatDateFieldValue(displayValue);
        }
        el.textContent = String(displayValue);
      } else {
        // Show field type label when empty (e.g., "(TEXT)", "(DATE)")
        const typeLabel = fieldType ? `(${fieldType.toUpperCase()})` : '(FIELD)';
        el.textContent = typeLabel;
      }
      // Remove visual field indicators for clean PDF output
      el.style.background = 'transparent';
      el.style.backgroundColor = 'transparent';
      el.style.borderBottom = 'none';
      el.style.color = 'inherit';
      // Also check for inner content wrapper
      const inner = el.querySelector('.fillable-field-placeholder-content');
      if (inner) {
        inner.style.background = 'transparent';
        inner.style.backgroundColor = 'transparent';
        inner.style.borderBottom = 'none';
        inner.style.color = 'inherit';
      }
    });

    // Handle signature blocks
    prose.querySelectorAll('[data-signature-block-id]').forEach((el) => {
      const id = el.getAttribute('data-signature-block-id');
      if (id == null) return;
      const sig = signatures[id];
      const meta = signatureMeta[id];

      if (sig || meta) {
        el.innerHTML = '';
        if (sig && typeof sig === 'string') {
          if (sig.startsWith('data:')) {
            const img = document.createElement('img');
            img.src = sig;
            img.alt = 'Signature';
            img.style.maxHeight = '60px';
            img.style.maxWidth = '200px';
            el.appendChild(img);
          } else {
            const span = document.createElement('span');
            span.textContent = sig;
            el.appendChild(span);
          }
        } else {
          const span = document.createElement('span');
          span.textContent = 'Signed';
          span.style.color = '#6b7280';
          span.style.fontStyle = 'italic';
          el.appendChild(span);
        }
        if (meta && (meta.signedAt || meta.signatureId)) {
          const line = document.createElement('div');
          line.style.fontSize = '11px';
          line.style.color = '#6b7280';
          line.style.marginTop = '4px';
          const parts = [];
          if (meta.signedAt) parts.push(`Signed on ${formatSignatureDate(meta.signedAt)}`);
          if (meta.signatureId) parts.push(`ID: ${meta.signatureId}`);
          line.textContent = parts.join(' · ');
          el.appendChild(line);
        }
      } else {
        const defaultSig = el.getAttribute('data-default-signature') || '';
        if (defaultSig) {
          el.innerHTML = '';
          if (defaultSig.startsWith('data:')) {
            const img = document.createElement('img');
            img.src = defaultSig;
            img.alt = 'Signature';
            img.style.maxHeight = '60px';
            img.style.maxWidth = '200px';
            el.appendChild(img);
          } else {
            const span = document.createElement('span');
            span.textContent = defaultSig;
            el.appendChild(span);
          }
          const signerName = el.getAttribute('data-default-signer-name') || '';
          const signedAt = el.getAttribute('data-default-signed-at') || '';
          if (signerName || signedAt) {
            const line = document.createElement('div');
            line.style.fontSize = '11px';
            line.style.color = '#6b7280';
            line.style.marginTop = '4px';
            const metaParts = [];
            if (signerName) metaParts.push(signerName);
            if (signedAt) metaParts.push(`Signed on ${formatSignatureDate(signedAt)}`);
            line.textContent = metaParts.join(' · ');
            el.appendChild(line);
          }
        }
      }

      // Remove signature block visual indicators (dashed border, background) for clean PDF
      el.style.border = 'none';
      el.style.background = 'transparent';
      el.style.backgroundColor = 'transparent';
      // Also target inner signature-block div
      const innerBlock = el.querySelector('.signature-block');
      if (innerBlock) {
        innerBlock.style.border = 'none';
        innerBlock.style.background = 'transparent';
        innerBlock.style.backgroundColor = 'transparent';
      }
    });
  }

  if (prose && submittedFormAnswers) {
    replaceFormBlocksWithAnswers(prose, submittedFormAnswers);
  }

  if (prose && (paymentComplete || paymentButtonUrl || (Array.isArray(paidLinkIds) && paidLinkIds.length))) {
    applyPaymentButtonsForPdf(prose, {
      paymentComplete,
      paidLinkIds,
      paymentAmountCents,
      paymentButtonUrl,
    });
  }

  if (prose && paymentComplete && paymentCompletedAt) {
    const paidNote = document.createElement('p');
    paidNote.style.cssText = 'font-size:12px;color:#16a34a;font-weight:600;margin:0 0 1rem 0;';
    paidNote.textContent = `Payment received on ${formatPaymentCompletedDisplay(paymentCompletedAt)}`;
    prose.insertBefore(paidNote, prose.firstChild);
  }

  // List items: convert to custom layout with inline markers for proper alignment
  if (prose) {
    prose.querySelectorAll('ol, ul').forEach((list) => {
      const isOrdered = list.tagName === 'OL';
      // Get the starting number from the ol's start attribute (defaults to 1)
      const startNum = isOrdered ? parseInt(list.getAttribute('start') || '1', 10) : 1;
      const items = list.querySelectorAll(':scope > li');
      items.forEach((li, index) => {
        // Remove native list styling
        li.style.listStyle = 'none';
        li.style.display = 'block';
        li.style.marginBottom = '0.75em';
        li.style.paddingLeft = '1.5em';
        li.style.textIndent = '-1.5em';

        // Calculate the correct number (check for value attribute on li, or use start + index)
        const liValue = li.getAttribute('value');
        const itemNumber = liValue ? parseInt(liValue, 10) : startNum + index;

        // Create marker span
        const marker = document.createElement('span');
        marker.style.display = 'inline';
        marker.style.marginRight = '0.5em';
        marker.style.fontWeight = 'normal';
        marker.textContent = isOrdered ? `${itemNumber}.` : '•';

        // Get first child and handle it
        const firstChild = li.firstElementChild;
        if (firstChild) {
          // Force first child to be inline and remove any margins
          firstChild.style.display = 'inline';
          firstChild.style.margin = '0';
          firstChild.style.padding = '0';
          li.insertBefore(marker, firstChild);
        } else if (li.firstChild) {
          // First child is a text node
          li.insertBefore(marker, li.firstChild);
        }

        // Style subsequent children as block with proper indent
        const children = Array.from(li.children);
        children.forEach((child, childIndex) => {
          if (childIndex > 1) { // Skip marker (0) and first content element (1)
            child.style.display = 'block';
            child.style.marginTop = '0.5em';
            child.style.textIndent = '0';
          }
        });
      });
      // Remove list padding since we're handling markers manually
      list.style.listStyle = 'none';
      list.style.paddingLeft = '0';
      list.style.marginLeft = '0';
    });
  }

  document.body.appendChild(container);
  await waitForImages(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: container.scrollWidth,
    });

    const containerRect = container.getBoundingClientRect();
    const containerHeightPx = container.scrollHeight;
    const containerWidthPx = container.scrollWidth || 794;
    const scale = 2;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const blockElements = container.querySelectorAll(BLOCK_SELECTOR);
    const blockBoundsRaw = [];
    const seenElements = new Set();
    // Safety buffer for block bottoms to account for font descenders and rendering differences
    const BLOCK_BOTTOM_BUFFER_PX = 8;
    blockElements.forEach((el) => {
      // Deduplicate elements (same element may match multiple selectors)
      if (seenElements.has(el)) return;
      seenElements.add(el);

      const rect = el.getBoundingClientRect();
      const topPx = rect.top - containerRect.top;
      const bottomPx = rect.bottom - containerRect.top;
      if (bottomPx > 0 && bottomPx > topPx) {
        // Add buffer to bottom to ensure text isn't cut through due to measurement differences
        blockBoundsRaw.push({ top: topPx, bottom: bottomPx + BLOCK_BOTTOM_BUFFER_PX });
      }
    });

    // Deduplicate bounds with very similar positions (within 2px)
    const blockBounds = [];
    const sortedBounds = blockBoundsRaw.sort((a, b) => a.top - b.top || a.bottom - b.bottom);
    sortedBounds.forEach((bound) => {
      const isDuplicate = blockBounds.some(
        (existing) => Math.abs(existing.top - bound.top) < 2 && Math.abs(existing.bottom - bound.bottom) < 2
      );
      if (!isDuplicate) {
        blockBounds.push(bound);
      }
    });

    const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    const pageWidthPt = pdf.internal.pageSize.getWidth();
    const pageHeightPt = pdf.internal.pageSize.getHeight();

    // Calculate usable content area with margins
    const contentWidthPt = pageWidthPt - MARGIN_LEFT_PT - MARGIN_RIGHT_PT;
    const usableHeightFirstPt = pageHeightPt - MARGIN_TOP_FIRST_PT - MARGIN_BOTTOM_PT;
    const usableHeightSubsequentPt = pageHeightPt - MARGIN_TOP_SUBSEQUENT_PT - MARGIN_BOTTOM_PT;

    const pxToPt = contentWidthPt / containerWidthPx;
    const imgHeightPt = (canvasHeight * contentWidthPt) / canvasWidth;
    const totalContentHeightPt = containerHeightPx * pxToPt;

    const breakPositionsPt = computeBreakPositions(
      blockBounds,
      pxToPt,
      usableHeightFirstPt,
      usableHeightSubsequentPt,
      totalContentHeightPt
    );

    for (let i = 0; i < breakPositionsPt.length - 1; i++) {
      const startPt = breakPositionsPt[i];
      const endPt = breakPositionsPt[i + 1];
      const sliceHeightPt = endPt - startPt;
      const isFirstPage = i === 0;
      const topMargin = isFirstPage ? MARGIN_TOP_FIRST_PT : MARGIN_TOP_SUBSEQUENT_PT;

      // Convert points to scaled pixels for canvas slicing
      // Use floor for start position to ensure we don't skip content at the top
      // Use ceil for height to ensure we capture all content at the bottom
      const startPxRaw = (startPt / pxToPt) * scale;
      const endPxRaw = (endPt / pxToPt) * scale;
      const startPxR = Math.floor(startPxRaw);
      // Calculate height from actual pixel positions to avoid cumulative rounding errors
      const sliceHeightPxR = Math.max(1, Math.ceil(endPxRaw) - startPxR);

      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvasWidth;
      sliceCanvas.height = sliceHeightPxR;
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, startPxR, canvasWidth, sliceHeightPxR, 0, 0, canvasWidth, sliceHeightPxR);

      const sliceDataUrl = sliceCanvas.toDataURL('image/png');
      if (i > 0) pdf.addPage();
      // Place slice with proper margins - use the actual slice height for proper scaling
      const actualSliceHeightPt = (sliceHeightPxR / scale) * pxToPt;
      pdf.addImage(sliceDataUrl, 'PNG', MARGIN_LEFT_PT, topMargin, contentWidthPt, actualSliceHeightPt, undefined, 'FAST');
    }

    if (breakPositionsPt.length < 2) {
      const imgData = canvas.toDataURL('image/png');
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * contentWidthPt) / imgProps.width;
      pdf.addImage(imgData, 'PNG', MARGIN_LEFT_PT, MARGIN_TOP_FIRST_PT, contentWidthPt, imgHeight, undefined, 'FAST');
    }

    // Note: Logo is already included in the HTML content via buildHeaderHtml,
    // so we don't call drawLogoOnFirstPage to avoid duplicate logos

    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    return { blob, url, filename: filenameOut };
  } finally {
    document.body.removeChild(container);
  }
}

function computeBreakPositions(blockBounds, pxToPt, firstPageHeightPt, subsequentPageHeightPt, totalContentHeightPt) {
  const boundsPt = blockBounds
    .map(({ top, bottom }) => ({ top: top * pxToPt, bottom: bottom * pxToPt }))
    .filter(({ bottom }) => bottom > 0)
    .sort((a, b) => a.top - b.top); // Sort by top position for consistent ordering

  const positions = [0];
  if (totalContentHeightPt <= 0) return positions;
  let currentStart = 0;
  let pageIndex = 0;

  // Minimum content height to avoid nearly-empty pages (about 2 lines of text)
  const MIN_PAGE_CONTENT_PT = 36;
  // Minimum visibility threshold - if a block would have less than this visible, move it entirely
  const MIN_BLOCK_VISIBILITY_PT = 20;

  while (currentStart < totalContentHeightPt) {
    // Use different page height for first page vs subsequent pages
    const pageHeightPt = pageIndex === 0 ? firstPageHeightPt : subsequentPageHeightPt;
    const pageEnd = currentStart + pageHeightPt;

    // Find ALL blocks that would be split by this page boundary
    // This includes blocks that:
    // 1. Start on this page and extend beyond (top >= currentStart && top < pageEnd && bottom > pageEnd)
    // 2. Started before this page and still extend beyond (top < currentStart && bottom > pageEnd) - continuing split
    const splitBlocks = boundsPt.filter(
      ({ top, bottom }) => top < pageEnd && bottom > pageEnd
    );

    // Also find blocks that would have minimal visibility at page bottom
    // (start near page end, would show only a tiny sliver before being cut)
    const nearEndBlocks = boundsPt.filter(
      ({ top, bottom }) =>
        top >= currentStart &&
        top < pageEnd &&
        bottom <= pageEnd &&
        (pageEnd - bottom) < MIN_BLOCK_VISIBILITY_PT &&
        (top > currentStart + MIN_PAGE_CONTENT_PT) // Only if there's enough content before
    );

    // Among split blocks, find the one we should break before
    // Prioritize blocks that START on this page (we can move them to next page)
    // versus blocks that started earlier (already being split, must continue)
    const movableBlocks = splitBlocks.filter(({ top }) => top >= currentStart && top < pageEnd);
    const continuingBlocks = splitBlocks.filter(({ top }) => top < currentStart);

    // Combine movable split blocks with near-end blocks for break consideration
    const blocksToConsider = [...movableBlocks];
    nearEndBlocks.forEach(block => {
      if (!blocksToConsider.some(b => Math.abs(b.top - block.top) < 2)) {
        blocksToConsider.push(block);
      }
    });

    if (blocksToConsider.length > 0) {
      // Find the block with the smallest top (earliest on page) - break before it
      const earliestBlock = blocksToConsider.reduce((earliest, block) =>
        block.top < earliest.top ? block : earliest
      );
      const blockHeight = earliestBlock.bottom - earliestBlock.top;

      // If block is taller than a page, we must split it at page boundary
      if (blockHeight > pageHeightPt) {
        const nextEnd = Math.min(pageEnd, totalContentHeightPt);
        if (nextEnd <= currentStart) break;
        positions.push(nextEnd);
        currentStart = nextEnd;
      } else {
        // Calculate safe break point: before the block's top with safety buffer
        // This ensures we don't cut through content due to rendering differences
        const safeBreakPoint = Math.max(0, earliestBlock.top - SAFETY_BUFFER_PT);

        // If breaking before the block would leave too little content on this page
        if (safeBreakPoint - currentStart < MIN_PAGE_CONTENT_PT && safeBreakPoint > currentStart) {
          // Very little content before this block - consider if block fits on next page
          if (blockHeight <= subsequentPageHeightPt) {
            // Block will fit on next page, so break before it even if page is short
            const breakPoint = Math.max(currentStart + 1, safeBreakPoint);
            positions.push(Math.min(breakPoint, totalContentHeightPt));
            currentStart = breakPoint;
          } else {
            // Block won't fit anywhere, split it at page boundary
            const nextEnd = Math.min(pageEnd, totalContentHeightPt);
            if (nextEnd <= currentStart) break;
            positions.push(nextEnd);
            currentStart = nextEnd;
          }
        } else {
          // Normal case: break at the safe point before the block
          const breakPoint = Math.max(currentStart + MIN_PAGE_CONTENT_PT, safeBreakPoint);
          positions.push(Math.min(breakPoint, totalContentHeightPt));
          currentStart = breakPoint;
        }
      }
    } else if (continuingBlocks.length > 0) {
      // All split blocks started before this page - they're already being split
      // Just continue to page boundary
      const nextEnd = Math.min(pageEnd, totalContentHeightPt);
      if (nextEnd <= currentStart) break;
      positions.push(nextEnd);
      currentStart = nextEnd;
    } else {
      // No block would be split - use the page end or last block bottom
      const candidates = boundsPt.filter(({ bottom }) => bottom > currentStart && bottom <= pageEnd);
      const bestEnd = candidates.length > 0 ? Math.max(...candidates.map((b) => b.bottom)) : pageEnd;
      const nextEnd = Math.min(bestEnd, totalContentHeightPt);
      if (nextEnd <= currentStart) break;
      positions.push(nextEnd);
      currentStart = nextEnd;
    }
    pageIndex++;
  }

  // Post-process: merge any tiny slices into the previous page
  // This prevents nearly-blank pages from being created anywhere in the document
  const mergedPositions = [positions[0]];
  for (let i = 1; i < positions.length; i++) {
    const sliceHeight = positions[i] - mergedPositions[mergedPositions.length - 1];
    if (sliceHeight >= MIN_PAGE_CONTENT_PT) {
      mergedPositions.push(positions[i]);
    } else {
      // Tiny slice - extend the previous position to include this content
      mergedPositions[mergedPositions.length - 1] = positions[i];
    }
  }

  // Ensure the final break position captures all content, but only if there's
  // meaningful content remaining (more than ~1 line of text). This prevents
  // creating blank pages due to floating point rounding or minimal trailing whitespace.
  const remainingContent = totalContentHeightPt - mergedPositions[mergedPositions.length - 1];
  if (remainingContent > MIN_PAGE_CONTENT_PT) {
    mergedPositions.push(totalContentHeightPt);
  } else if (remainingContent > 0 && mergedPositions.length > 1) {
    // Tiny remainder - extend the last page break to include it rather than creating a new page
    mergedPositions[mergedPositions.length - 1] = totalContentHeightPt;
  }
  return mergedPositions;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function normalizeComparableText(s) {
  return String(s || '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strip common markdown wrappers for comparing heading vs duplicate line text. */
function stripMarkdownBold(s) {
  return normalizeComparableText(String(s || '').replace(/\*\*/g, ''));
}

/**
 * Remove stray paragraphs that repeat the previous heading as raw markdown (e.g. "# Title")
 * so PDF export matches what users see when the heading is already rendered as <h1>…</h1>.
 */
function removeDuplicateMarkdownHeadingLines(prose) {
  const blocks = prose.querySelectorAll('h1, h2, h3, h4, h5, h6, p');
  let lastHeadingText = null;
  const toRemove = [];
  blocks.forEach((el) => {
    const tag = el.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) {
      lastHeadingText = normalizeComparableText(el.textContent);
      return;
    }
    if (tag === 'p' && lastHeadingText) {
      const t = normalizeComparableText(el.textContent);
      const m = t.match(/^#{1,6}\s+(.+)$/);
      if (m) {
        const rest = normalizeComparableText(m[1]);
        if (
          rest === lastHeadingText
          || stripMarkdownBold(rest) === stripMarkdownBold(lastHeadingText)
        ) {
          toRemove.push(el);
          lastHeadingText = null;
          return;
        }
      }
      lastHeadingText = null;
    }
  });
  toRemove.forEach((el) => el.remove());
}

